import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BlogHeroArt from '../components/BlogHeroArt';
import { getPostBySlug, blogPosts } from '../data/blogPosts';
import { tokens } from '../theme';

// Infographic components
const StatHighlight: React.FC<{ stats: Array<{ value: string; label: string; color: string }> }> = ({ stats }) => (
  <Box sx={{
    display: 'flex',
    gap: 3,
    justifyContent: 'center',
    flexWrap: 'wrap',
    my: 5,
    py: 4,
    px: 3,
    bgcolor: 'rgba(199, 91, 57, 0.06)',
    borderRadius: '16px',
    border: `1px solid ${tokens.border}`,
  }}>
    {stats.map((stat, i) => (
      <Box key={i} sx={{ textAlign: 'center', minWidth: 140 }}>
        <Typography sx={{
          fontSize: '2.5rem',
          fontWeight: 600,
          fontFamily: 'Fraunces, Georgia, serif',
          color: stat.color === 'teal' ? tokens.copper : tokens.ink,
          lineHeight: 1,
        }}>
          {stat.value}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>
          {stat.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const ComparisonBox: React.FC<{
  leftTitle: string;
  leftItems: string[];
  rightTitle: string;
  rightItems: string[];
}> = ({ leftTitle, leftItems, rightTitle, rightItems }) => (
  <Grid container spacing={3} sx={{ my: 4 }}>
    <Grid size={{ xs: 12, md: 6 }}>
      <Paper sx={{
        p: 3,
        bgcolor: 'rgba(179, 38, 30, 0.05)',
        border: `1px solid ${tokens.border}`,
        borderRadius: '16px',
        height: '100%',
      }}>
        <Typography sx={{ fontWeight: 700, mb: 2, color: tokens.error }}>
          {leftTitle}
        </Typography>
        {leftItems.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <Box sx={{ color: tokens.error, fontSize: '1.2rem', lineHeight: 1 }}>✕</Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item}</Typography>
          </Box>
        ))}
      </Paper>
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <Paper sx={{
        p: 3,
        bgcolor: 'rgba(47, 93, 80, 0.08)',
        border: `1px solid ${tokens.border}`,
        borderRadius: '16px',
        height: '100%',
      }}>
        <Typography sx={{ fontWeight: 700, mb: 2, color: tokens.sage }}>
          {rightTitle}
        </Typography>
        {rightItems.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <Box sx={{ color: tokens.sage, fontSize: '1.2rem', lineHeight: 1 }}>✓</Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item}</Typography>
          </Box>
        ))}
      </Paper>
    </Grid>
  </Grid>
);

const TimelineBox: React.FC<{ steps: Array<{ time: string; status: string; description: string }> }> = ({ steps }) => (
  <Box sx={{ my: 4, py: 3 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {steps.map((step, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box sx={{
            width: 100,
            flexShrink: 0,
            pr: 2,
            textAlign: 'right',
            borderRight: '2px solid',
            borderColor: step.status === 'optimal' ? tokens.sage :
                        step.status === 'declining' ? tokens.warning :
                        step.status === 'critical' ? tokens.error : tokens.textSecondary,
            py: 2,
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{step.time}</Typography>
          </Box>
          <Box sx={{
            pl: 3,
            py: 2,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: step.status === 'optimal' ? tokens.sage :
                      step.status === 'declining' ? tokens.warning :
                      step.status === 'critical' ? tokens.error : tokens.textSecondary,
            }
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{step.description}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const ChatExample: React.FC<{ title: string; messages: Array<{ from: string; text: string }> }> = ({ title, messages }) => (
  <Box sx={{ my: 4 }}>
    <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}>{title}</Typography>
    <Paper sx={{
      p: 3,
      bgcolor: tokens.ink,
      borderRadius: '16px',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {messages.map((msg, i) => (
        <Box key={i} sx={{
          display: 'flex',
          justifyContent: msg.from === 'bot' ? 'flex-start' : 'flex-end',
          mb: 2,
          '&:last-child': { mb: 0 },
        }}>
          <Box sx={{
            maxWidth: '80%',
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            bgcolor: msg.from === 'bot' ? 'rgba(111, 174, 151, 0.14)' : 'rgba(217, 119, 87, 0.14)',
            border: `1px solid ${msg.from === 'bot' ? 'rgba(111, 174, 151, 0.3)' : 'rgba(217, 119, 87, 0.3)'}`,
          }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#F0EDE8' }}>{msg.text}</Typography>
          </Box>
        </Box>
      ))}
    </Paper>
  </Box>
);

const DataTable: React.FC<{ headers: string[]; rows: string[][] }> = ({ headers, rows }) => (
  <Box sx={{ my: 4, overflowX: 'auto' }}>
    <Paper sx={{
      bgcolor: '#FFFFFF',
      border: `1px solid ${tokens.border}`,
      borderRadius: '16px',
      overflow: 'hidden',
    }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <Box component="thead" sx={{ bgcolor: tokens.bandAlt }}>
          <Box component="tr">
            {headers.map((h, i) => (
              <Box component="th" key={i} sx={{
                px: 2,
                py: 1.5,
                textAlign: 'left',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderBottom: `1px solid ${tokens.border}`,
              }}>
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, i) => (
            <Box component="tr" key={i} sx={{
              '&:hover': { bgcolor: tokens.bgDefault },
            }}>
              {row.map((cell, j) => (
                <Box component="td" key={j} sx={{
                  px: 2,
                  py: 1.5,
                  fontSize: '0.85rem',
                  color: 'text.secondary',
                  borderBottom: i < rows.length - 1 ? `1px solid ${tokens.border}` : 'none',
                }}>
                  {cell}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  </Box>
);

// Parse markdown-like content with custom infographic tags
const renderContent = (content: string) => {
  const sections = content.split('\n\n');
  const elements: React.ReactNode[] = [];

  sections.forEach((section, index) => {
    const trimmed = section.trim();

    if (trimmed.startsWith('# ')) {
      elements.push(
        <Typography key={index} variant="h2" sx={{ mt: 6, mb: 3, fontWeight: 700 }}>
          {trimmed.slice(2)}
        </Typography>
      );
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <Typography key={index} variant="h4" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          {trimmed.slice(3)}
        </Typography>
      );
    } else if (trimmed.startsWith('### ')) {
      elements.push(
        <Typography key={index} variant="h6" sx={{ mt: 4, mb: 1.5, fontWeight: 600 }}>
          {trimmed.slice(4)}
        </Typography>
      );
    } else if (trimmed === '---') {
      elements.push(<Divider key={index} sx={{ my: 4, borderColor: tokens.border }} />);
    } else if (trimmed.startsWith('<infographic type="stat-highlight">')) {
      // Parse stat-highlight infographic
      const stats: Array<{ value: string; label: string; color: string }> = [];
      const statMatches = Array.from(trimmed.matchAll(/<stat value="([^"]+)" label="([^"]+)" color="([^"]+)" \/>/g));
      for (const match of statMatches) {
        stats.push({ value: match[1], label: match[2], color: match[3] });
      }
      if (stats.length > 0) {
        elements.push(<StatHighlight key={index} stats={stats} />);
      }
    } else if (trimmed.startsWith('<infographic type="comparison">')) {
      // Parse comparison infographic
      const leftMatch = trimmed.match(/<column title="([^"]+)" items=\{(\[[^\]]+\])\} negative \/>/);
      const rightMatch = trimmed.match(/<column title="([^"]+)" items=\{(\[[^\]]+\])\} positive \/>/);
      if (leftMatch && rightMatch) {
        try {
          const leftItems = JSON.parse(leftMatch[2].replace(/'/g, '"'));
          const rightItems = JSON.parse(rightMatch[2].replace(/'/g, '"'));
          elements.push(
            <ComparisonBox
              key={index}
              leftTitle={leftMatch[1]}
              leftItems={leftItems}
              rightTitle={rightMatch[1]}
              rightItems={rightItems}
            />
          );
        } catch (e) {
          // Skip malformed infographic
        }
      }
    } else if (trimmed.startsWith('<infographic type="before-after">')) {
      // Parse before-after (similar to comparison)
      const beforeMatch = trimmed.match(/<before title="([^"]+)" items=\{(\[[^\]]+\])\} \/>/);
      const afterMatch = trimmed.match(/<after title="([^"]+)" items=\{(\[[^\]]+\])\} \/>/);
      if (beforeMatch && afterMatch) {
        try {
          const beforeItems = JSON.parse(beforeMatch[2].replace(/'/g, '"'));
          const afterItems = JSON.parse(afterMatch[2].replace(/'/g, '"'));
          elements.push(
            <ComparisonBox
              key={index}
              leftTitle={beforeMatch[1]}
              leftItems={beforeItems}
              rightTitle={afterMatch[1]}
              rightItems={afterItems}
            />
          );
        } catch (e) {
          // Skip malformed infographic
        }
      }
    } else if (trimmed.startsWith('<infographic type="timeline">')) {
      // Parse timeline
      const steps: Array<{ time: string; status: string; description: string }> = [];
      const stepMatches = Array.from(trimmed.matchAll(/<step time="([^"]+)" status="([^"]+)" description="([^"]+)" \/>/g));
      for (const match of stepMatches) {
        steps.push({ time: match[1], status: match[2], description: match[3] });
      }
      if (steps.length > 0) {
        elements.push(<TimelineBox key={index} steps={steps} />);
      }
    } else if (trimmed.startsWith('<infographic type="chat-example"')) {
      // Parse chat example
      const titleMatch = trimmed.match(/title="([^"]+)"/);
      const messages: Array<{ from: string; text: string }> = [];
      const msgMatches = Array.from(trimmed.matchAll(/<message from="([^"]+)"[^>]*>([^<]+)<\/message>/g));
      for (const match of msgMatches) {
        messages.push({ from: match[1], text: match[2] });
      }
      if (messages.length > 0) {
        elements.push(<ChatExample key={index} title={titleMatch?.[1] || ''} messages={messages} />);
      }
    } else if (trimmed.startsWith('| ') && trimmed.includes(' | ')) {
      // Parse markdown tables
      const lines = trimmed.split('\n').filter(l => l.trim().startsWith('|'));
      if (lines.length >= 2) {
        const headerRow = lines[0].split('|').filter(c => c.trim()).map(c => c.trim());
        const dataRows = lines.slice(2).map(line =>
          line.split('|').filter(c => c.trim()).map(c => c.trim())
        );
        elements.push(<DataTable key={index} headers={headerRow} rows={dataRows} />);
      }
    } else if (trimmed.startsWith('> ')) {
      // Blockquote
      elements.push(
        <Box key={index} sx={{
          my: 3,
          pl: 3,
          py: 2,
          borderLeft: `3px solid ${tokens.copper}`,
          bgcolor: 'rgba(199, 91, 57, 0.06)',
          borderRadius: '0 8px 8px 0',
        }}>
          <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {trimmed.slice(2)}
          </Typography>
        </Box>
      );
    } else if (trimmed.startsWith('1. ') || trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      // Lists
      const items = trimmed.split('\n');
      const isOrdered = trimmed.startsWith('1. ');
      elements.push(
        <Box key={index} component={isOrdered ? 'ol' : 'ul'} sx={{ my: 2, pl: 3 }}>
          {items.map((item, i) => {
            const text = item.replace(/^\d+\.\s*|-\s*|\*\s*/, '');
            return (
              <Box component="li" key={i} sx={{ mb: 1, color: 'text.secondary' }}>
                <Typography variant="body1" component="span"
                  dangerouslySetInnerHTML={{
                    __html: text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                  }}
                />
              </Box>
            );
          })}
        </Box>
      );
    } else if (trimmed.startsWith('**') && trimmed.includes(':**')) {
      // Bold label with content
      elements.push(
        <Typography key={index} variant="body1" sx={{ mb: 2, color: 'text.secondary' }}
          dangerouslySetInnerHTML={{
            __html: trimmed.replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #1C1917">$1</strong>')
          }}
        />
      );
    } else if (trimmed && !trimmed.startsWith('<')) {
      // Regular paragraph
      elements.push(
        <Typography key={index} variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}
          dangerouslySetInnerHTML={{
            __html: trimmed.replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #1C1917">$1</strong>')
          }}
        />
      );
    }
  });

  return elements;
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug || '');

  if (!post) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Navbar />
        <Container maxWidth="md" sx={{ pt: 20, pb: 10, textAlign: 'center' }}>
          <Typography variant="h2" gutterBottom>Post Not Found</Typography>
          <Button component={RouterLink} to="/blog" startIcon={<ArrowBack />}>
            Back to Blog
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      {/* Header */}
      <Box sx={{ pt: 15, pb: 6 }}>
        <Container maxWidth="md">
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBack />}
            sx={{ mb: 4, color: 'text.secondary' }}
          >
            Back to Blog
          </Button>

          <Chip label={post.category} color="primary" sx={{ mb: 2 }} />

          <Typography variant="h1" sx={{ mb: 3, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            {post.title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, color: 'text.secondary' }}>
            <Typography variant="body2">{post.author}</Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">{post.date}</Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">{post.readTime}</Typography>
          </Box>

          {/* Hero art */}
          <Box
            sx={{
              mt: 4,
              border: `1px solid ${tokens.border}`,
              borderRadius: `${tokens.radiusCard}px`,
              overflow: 'hidden',
              boxShadow: tokens.shadowSoft,
            }}
          >
            <BlogHeroArt
              slug={post.slug}
              title={post.title}
              height={{ xs: 200, md: 300 }}
            />
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ pb: 10 }}>
        <Box sx={{
          '& strong': { color: 'text.primary' },
        }}>
          {renderContent(post.content)}
        </Box>

        {/* CTA */}
        <Box sx={{
          mt: 8,
          p: 4,
          textAlign: 'center',
          bgcolor: 'rgba(199, 91, 57, 0.06)',
          borderRadius: '16px',
          border: `1px solid ${tokens.border}`,
        }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Ready to capture more leads?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            Start responding to every inquiry in under 30 seconds, 24/7.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://app.cleolead.com/login"
          >
            Start Free Trial
          </Button>
        </Box>

        {/* Navigation */}
        <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          {prevPost ? (
            <Button
              component={RouterLink}
              to={`/blog/${prevPost.slug}`}
              startIcon={<ArrowBack />}
              sx={{ color: 'text.secondary' }}
            >
              {prevPost.title.slice(0, 30)}...
            </Button>
          ) : <Box />}
          {nextPost && (
            <Button
              component={RouterLink}
              to={`/blog/${nextPost.slug}`}
              endIcon={<ArrowForward />}
              sx={{ color: 'text.secondary' }}
            >
              {nextPost.title.slice(0, 30)}...
            </Button>
          )}
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default BlogPostPage;
